import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { bmxurl, getAccess } from 'src/globals';
import { MessageService } from 'primeng/api';
import { users_returndata } from 'src/types/user_returndata';
import { GlobalService } from '../_services/global.service';
import { BusyService } from '../_services/busy.service';

@Component({
  selector: 'app-packagebookingfees',
  templateUrl: './packagebookingfees.component.html',
  styleUrls: ['./packagebookingfees.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PackagebookingfeesComponent implements OnInit {
  VenueID: string = localStorage.getItem('SmartVenueID')!;
  AccessAllowed: boolean = false;
  CompletedLoading: boolean = false;
  httpResponse: any;

  packages: any[] = [];
  feeDropdownOptions: { label: string; value: string | null }[] = [];
  private feeColourMap = new Map<string, string>();
  private feeNameMap = new Map<string, string>();

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private globalService: GlobalService,
    private busyService: BusyService
  ) {
    let userid = localStorage.getItem('SmartUserID');
    this.http.get(bmxurl + 'User/' + userid).subscribe({
      next: (response: any) => {
        this.httpResponse = response;
        if (this.httpResponse.succeeded) {
          let user = this.httpResponse.data;
          if (getAccess('BOOKINGFEES', user)) {
            this.AccessAllowed = true;
          }
        }
        this.CompletedLoading = true;
        this.loadData();
      },
      error: () => {
        this.CompletedLoading = true;
      }
    });
  }

  ngOnInit(): void {
    this.globalService.setSmallScreenMenu(false);
  }

  loadData(): void {
    this.busyService.busy();
    this.http.get(bmxurl + 'BookingFeeList/' + this.VenueID + '/false').subscribe({
      next: (feeResponse: any) => {
        if (feeResponse.succeeded) {
          this.feeColourMap.clear();
          this.feeNameMap.clear();
          (feeResponse.data ?? []).forEach((f: any) => {
            this.feeColourMap.set(f.bfid, f.colour || '#4CAF50');
            this.feeNameMap.set(f.bfid, f.name + (f.isdefault ? ' (Default)' : ''));
          });
          this.feeDropdownOptions = [
            { label: 'No booking fee', value: null },
            ...(feeResponse.data ?? []).map((f: any) => ({
              label: f.name + (f.isdefault ? ' (Default)' : ''),
              value: f.bfid,
            }))
          ];
        } else {
          this.feeDropdownOptions = [{ label: 'No booking fee', value: null }];
        }
        this.loadPackages();
      },
      error: () => {
        this.feeDropdownOptions = [{ label: 'No booking fee', value: null }];
        this.loadPackages();
      }
    });
  }

  loadPackages(): void {
    this.http.get(bmxurl + 'packagelist/' + this.VenueID + '/false').subscribe({
      next: (response: any) => {
        this.busyService.idle();
        if (response.succeeded) {
          this.packages = (response.data ?? [])
            .map((pkg: any) => ({
              ...pkg,
              _bookingfeeid: pkg.bookingfeeid ?? null,
            }))
            .sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? ''));
        } else {
          this.messageService.add({ severity: 'warn', summary: 'Warning', detail: response.messages?.[0] ?? 'Could not load packages' });
        }
      },
      error: () => {
        this.busyService.idle();
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load packages' });
      }
    });
  }

  getFeeName(bookingfeeid: string | null): string {
    if (!bookingfeeid) return '';
    return this.feeNameMap.get(bookingfeeid) ?? this.feeDropdownOptions.find(f => f.value === bookingfeeid)?.label ?? '';
  }

  getFeeColour(bookingfeeid: string | null): string {
    if (!bookingfeeid) return 'transparent';
    return this.feeColourMap.get(bookingfeeid) ?? 'transparent';
  }

  onFeeChange(pkg: any): void {
    const updatedPkg = { ...pkg, bookingfeeid: pkg._bookingfeeid ?? null };
    this.busyService.busy();
    this.http.put(bmxurl + 'package', updatedPkg).subscribe({
      next: (response: any) => {
        this.busyService.idle();
        if (response.succeeded) {
          pkg.bookingfeeid = pkg._bookingfeeid;
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: `Booking fee updated for ${pkg.name}` });
        } else {
          pkg._bookingfeeid = pkg.bookingfeeid ?? null;
          this.messageService.add({ severity: 'error', summary: 'Not Saved', detail: response.messages?.[0] ?? 'Save failed' });
        }
      },
      error: () => {
        this.busyService.idle();
        pkg._bookingfeeid = pkg.bookingfeeid ?? null;
        this.messageService.add({ severity: 'error', summary: 'Not Saved', detail: 'Error saving booking fee assignment' });
      }
    });
  }
}
